/**
 * Agent picker for the preview bar (D2-SEC-011, #3944): choose an agent and
 * see the CRM exactly as they see it, read only. Renders nothing for a user
 * the server will not allow (see `useViewAsTargets`).
 */
import { useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { beginViewAs } from "../../api/viewAs";
import { useViewAsTargets } from "./useViewAsTargets";

export function ViewAsPicker() {
  const targets = useViewAsTargets();
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  if (!targets || targets.length === 0) return null;

  async function handleGo() {
    const id = Number(selected);
    if (!id) return;
    setBusy(true);
    try {
      await beginViewAs(id);
    } catch {
      setBusy(false);
    }
  }

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <label htmlFor="view-as-picker" className="sr-only">
        View as agent
      </label>
      <select
        id="view-as-picker"
        data-testid="view-as-picker"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        className="min-h-[44px] max-w-[10rem] rounded-md border border-white/20 bg-navy-light px-2 text-base text-white sm:max-w-[14rem] sm:text-sm"
      >
        <option value="">View as agent…</option>
        {targets.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={handleGo}
        disabled={!selected || busy}
        aria-label="Start viewing as the selected agent (read only)"
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md border border-white/30 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-50 sm:text-sm"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
        <span>View</span>
      </button>
    </div>
  );
}
